import {Injectable, Input} from '@angular/core';
import {jwtAuthService} from '../api/module/erp/services/auth/jwtAuthService';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse,
} from '@angular/common/http';
import {throwError, Observable, of} from 'rxjs';
import {map, catchError} from 'rxjs/operators';
import {ErrorResponse} from '../api/model/common/error-response.model';
import {ToastrService} from 'ngx-toastr';
import {AppConfigurationService} from '../api/module/erp/services/base/appConfiguration.service';
import {Router} from '@angular/router';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private jwtAuthService: jwtAuthService,
    private toastr: ToastrService,
    private appConfigurationService: AppConfigurationService,
    private router: Router,
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler) {
    //recupara o token no request
    const token = this.jwtAuthService.getAuthorizationToken();
    if (req.url.includes('/cotacao/visualizar/')) {
      return next.handle(req); // Não adiciona o token porque esta sendo usado como lik externo
    }

    //intercepta qualquer request que esta sendo feito
    let request: HttpRequest<any> = req;
    //&& !this.contaService.isTokenExpired(token)
    if (token && !this.jwtAuthService.IsTokenExpirado(token)) {
      // O request é imutavel, ou seja, não é possível mudar nada
      // Faço o clone para conseguir mudar as propriedades
      // Passo o token de autenticação no header
      request = req.clone({
        headers: req.headers.set('Authorization', `Bearer ${token}`),
      });
    }

    // retorno o request com o erro tratado
    return next
      .handle(request)
      .pipe(catchError(error => this.handleError(error)));
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ErrorEvent) {
      // Erro de client-side ou de rede
      this.toastr.error(error.error.message);
      console.error('Ocorreu um erro:', error.error.message);
      return throwError(
        () => new Error(error.error.message || 'Erro no servidor'),
      );
    }

    if (error.status === 401) {
      document.location.reload;
    }

    if (error.status === 400) {
      let errorResponse = new ErrorResponse();

      // Verifique se o erro contém a chave 'errors'
      if (error.error?.errors) {
        // Caso a mensagem de erro seja um array ou objeto, converta para string
        errorResponse.message = Array.isArray(error.error['errors'])
          ? error.error['errors'].join(', ') // Junte as mensagens de erro se for um array
          : JSON.stringify(error.error['errors']); // Caso seja um objeto, converta para string
      }
      // Verifique se o erro contém a chave 'data'
      else if (error.error?.data) {
        errorResponse.message = Array.isArray(error.error['data'])
          ? error.error['data'].join(', ') // Junte as mensagens de erro se for um array
          : JSON.stringify(error.error['data']); // Caso seja um objeto, converta para string
      }
      // Caso o erro tenha uma chave 'error'
      else if (error.error) {
        errorResponse.message = error.error;
      } else {
        // Se nenhum dos casos acima se aplicar, mostre um erro genérico
        errorResponse.message = 'Ocorreu um erro inesperado.';
      }

      // Retorne o erro com a mensagem e o status
      return throwError(() => ({
        message: errorResponse.message,
        status: error.status,
      }));
    }

    if (error.status === 403) {
      var errorResponse = new ErrorResponse();

      errorResponse.message =
        'Você não tem permissão para realizar esta ação, favor contactar o suporte!';

      this.router.navigate(['/acesso-negado']);

      if (error.error['data']) {
        errorResponse.message = error.error['data'];
      }

      return throwError(() => ({
        message: errorResponse.message,
        status: error.status,
      }));
    }

    if (error.status === 404) {
      const errorResponse = new ErrorResponse();

      // Verifique se existe o campo 'errors' e manipule adequadamente
      if (error.error?.errors) {
        // Se for um array, junte os itens em uma única string
        errorResponse.message = Array.isArray(error.error['errors'])
          ? error.error['errors'].join(', ') // Junte as mensagens de erro
          : JSON.stringify(error.error['errors']); // Se for um objeto, converta para string
      }
      // Verifique se existe o campo 'data'
      else if (error.error?.data) {
        // Se for um array, junte os itens em uma única string
        errorResponse.message = Array.isArray(error.error['data'])
          ? error.error['data'].join(', ') // Junte as mensagens de erro
          : JSON.stringify(error.error['data']); // Se for um objeto, converta para string
      }
      // Caso o erro tenha uma chave 'error'
      else if (error.error?.error) {
        errorResponse.message = error.error['error'];
      } else {
        // Caso contrário, exiba um erro genérico
        errorResponse.message = 'Recurso não encontrado.';
      }

      // Retorne o erro com a mensagem
      return throwError(() => ({
        message: errorResponse.message,
        status: error.status,
      }));
    }

    if (error.status === 422) {
      var errorResponse = new ErrorResponse();

      if (error.error['errors']) {
        errorResponse.message = error.error['errors'];
      } else if (error.error['data']) {
        errorResponse.message = error.error['data'];
      }

      return throwError(() => new Error(errorResponse.message));
    }

    if (error.status === 500) {
      const errorResponse = new ErrorResponse();

      // Verifique se existe o campo 'errors' e manipule adequadamente
      if (error.error?.errors) {
        // Se for um array, junte os itens em uma única string
        errorResponse.message = Array.isArray(error.error['errors'])
          ? error.error['errors'].join(', ') // Junte as mensagens de erro
          : JSON.stringify(error.error['errors']); // Se for um objeto, converta para string
      }
      // Verifique se existe o campo 'data'
      else if (error.error?.data) {
        // Se for um array, junte os itens em uma única string
        errorResponse.message = Array.isArray(error.error['data'])
          ? error.error['data'].join(', ') // Junte as mensagens de erro
          : JSON.stringify(error.error['data']); // Se for um objeto, converta para string
      }
      // Caso o erro tenha uma chave 'error'
      else if (error.error?.error) {
        errorResponse.message = error.error['error'];
      } else {
        // Caso contrário, exiba um erro genérico
        errorResponse.message = 'Recurso não encontrado.';
      }

      // Retorne o erro com a mensagem
      return throwError(() => ({
        message: errorResponse.message,
        status: error.status,
      }));
    }

    if (error.status === 551) {
      var errorResponse = new ErrorResponse();

      errorResponse.message = '';
      if (error.error != null && error.error != '') {
        errorResponse.message += error.error;
      }

      return throwError(() => new Error(errorResponse.message));
    }

    return throwError(
      () =>
        new Error(
          'Ocorreu um erro, tente novamente: ' + `${JSON.stringify(error)}`,
        ),
    );
  }
}
